import "@core/i18n/dayjs-requirements";
import { TranslationsManager } from "@core/i18n/translations-manager";
import { getLogger } from "@core/logging/logger";
import { Service } from "@core/services/service";
import { LocaleManager } from "@domain/localization/locale-manager";
import { builtInWording } from "@wording/builtInWording";
import dayjs from "dayjs";
import { intlLocaleToLocale } from "@core/i18n/locale-helper";

type Locale = keyof typeof builtInWording;
const isLocale = (string: string): string is Locale => string in parentLocale;

const parentLocale: Record<string, string> = {
	"de-DE": "de",
	"en-CA": "en-ca",
	"en-US": "en",
	"es-AR": "es-mx",
	"es-CL": "es-mx",
	"es-ES": "es",
	"es-UY": "es-mx",
	"fr-CA": "fr-ca",
	"fr-FR": "fr",
	"it-IT": "it",
	"pl-PL": "pl",
	"hu-HU": "hu",
	"pt-BR": "pt-br",
	"de-CH": "de",
	"fr-CH": "fr",
	"it-CH": "it",
	en: "en",
};

export class DayjsLibConfiguration implements Service {
	private readonly logger = getLogger("⏲️ [Dayjs Lib Configuration]");

	constructor(
		private readonly localeManager: LocaleManager,
		private readonly translationsManager: TranslationsManager,
	) { }

	async init(): Promise<void> {
		this.setDayjsLocale(this.localeManager.locale.get());
		this.localeManager.locale.subscribe(locale => {
			this.setDayjsLocale(locale);
		});
		this.translationsManager.weekdaysConfig.subscribe(() => {
			this.setDayjsLocale(this.localeManager.locale.get());
		});
	}

	private setDayjsLocale(locale: string) {
		if (!isLocale(locale)) return;

		const translations = this.translationsManager.getLocaleWordings(locale);
		const localeList = dayjs.Ls;
		const parentLocal = parentLocale[locale];

		const localeConfiguration = {
			weekStart: this.getWeekStart(locale),
			weekdaysMin: this.getWeekdaysMin(locale),
			parentLocale: parentLocal,
			relativeTime: {
				...localeList[parentLocal].relativeTime,
				m: translations["global.relativeTime.minute"],
				mm: translations["global.relativeTime.minutes"],
				h: translations["global.relativeTime.hour"],
				hh: translations["global.relativeTime.hours"],
				d: translations["global.relativeTime.day"],
				dd: translations["global.relativeTime.days"],
				w: translations["global.relativeTime.week"],
				ww: translations["global.relativeTime.weeks"],
				M: translations["global.relativeTime.month"],
				MM: translations["global.relativeTime.months"],
				y: translations["global.relativeTime.year"],
				yy: translations["global.relativeTime.years"],
			},
			formats: localeList[parentLocal].formats,
		};
		dayjs.updateLocale(parentLocal, localeConfiguration);
		dayjs.locale(parentLocal);
		this.logger.info("Dayjs locale set to", dayjs.locale());
	}

	private getWeekdaysMin(currentLocale: string) {
		const weekdaysConfig = this.translationsManager.weekdaysConfig.get();
		return weekdaysConfig.initials[intlLocaleToLocale(currentLocale)];
	}

	private getWeekStart(currentLocale: string): number {
		const weekdaysConfig = this.translationsManager.weekdaysConfig.get();
		return weekdaysConfig.firstDayOfWeek[intlLocaleToLocale(currentLocale)];
	}
}
